import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';

import { of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import { DemoModeService } from './demo-mode.service';

import type { DemoRequest } from './demo-backend';

const DEMO_LATENCY_MS = 180;

function toDemoRequest(method: string, urlWithParams: string, body: unknown): DemoRequest {
  const [path, query] = urlWithParams.split('?');
  return { method, url: path, params: new URLSearchParams(query ?? ''), body };
}

function asRequestedType(body: unknown, responseType: string): unknown {
  if (responseType !== 'text' || typeof body === 'string') {
    return body;
  }
  if (typeof body === 'object' && body !== null && 'file' in body) {
    return String((body as { file: unknown }).file);
  }
  return JSON.stringify(body);
}

/**
 * While a demo session is running this interceptor NEVER calls next(): every
 * request is answered from local fixtures, including the ones it has no
 * fixture for. That is the isolation guarantee — no socket is opened, so demo
 * mode cannot read or write any real company's data. Must stay FIRST in the
 * chain (app.config.ts) so neither the auth nor the tenant interceptor runs.
 */
export const demoInterceptor: HttpInterceptorFn = (request, next) => {
  const demoMode = inject(DemoModeService);

  if (!demoMode.active()) {
    return next(request);
  }

  const demoRequest = toDemoRequest(request.method, request.urlWithParams, request.body);
  const resolved = demoMode.resolve(demoRequest);

  if (!resolved) {
    console.warn(`[demo] no fixture for ${request.method} ${demoRequest.url} — answering 404`);
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 404,
          statusText: 'Not Found (demo)',
          url: demoRequest.url,
          error: { message: '[demo] no fixture for this request' },
        })
    );
  }

  return of(
    new HttpResponse({
      status: resolved.status,
      url: demoRequest.url,
      body: asRequestedType(resolved.body, request.responseType),
    })
  ).pipe(delay(DEMO_LATENCY_MS));
};
